// components/SalespersonTabSet/SalespersonTabSet.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Text, Group, Stack } from '@mantine/core';
import { IconCircleFilled, IconCircle } from '@tabler/icons-react';

import { useSearchParams } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
 
import SalespersonSelect from './SalespersonSelect';
import SalespersonProfile from './SalespersonProfile';
import CalendarMasterDetail from '../../CalendarMasterDetail/CalendarMasterDetail'; // Adjust the path as needed
import '@fontsource/orbitron/700.css'; // For bold weight

// Import your GraphQL query
import { GET_DAILY_SALESPERSON_SALES_PRODUCTION, GET_DAILY_SALESPERSON_PRICING_PRODUCTION, GET_ASSIGNED_ACCOUNTS } from '../../../queries'; // Adjust the path as needed
import SalespersonPerformance from './SalespersonPerformance';
import SalespersonCustomerSelect from './SalespersonCustomerSelect';

import SalespersonQuoteDetail from './SalespersonQuoteDetail.js';
import SalespersonCallDetail from './SalespersonCallDetail.js';
import SalespersonSalesDetail from './SalespersonSalesDetail.js';
import SalespersonRepairsRFQsDetail from './SalespersonRepairsRFQsDetail.js';
import SalespersonPurchaseRFQsDetail from './SalespersonPurchaseRFQsDetail.js';
import SalespersonPEDetail from './SalespersonPEDetail.js';
import SalespersonProficiency from './SalespersonProficiency';
import SalesHistoryChart from './SalesHistoryChart.jsx'; // Import the new chart component

const SalespersonTabSet = () => {
  const [activeTab, setActiveTab] = useState('0');
  const [activeCalendarTab, setActiveCalendarTab] = useState(0);
  const [selectedSalesperson, setSelectedSalesperson] = useState(null);

  const [searchParams] = useSearchParams();
  const defaultSalespersonCode = searchParams.get('code'); // Ensure 'code' is used
  const defaultTab = searchParams.get('tab');
  const [selectedCustomer, setSelectedCustomer] = useState('');

  const { data: assignedAccountsData, loading: loadingAccounts, error: errorAccounts } = useQuery(GET_ASSIGNED_ACCOUNTS, {
    variables: { ccode: selectedSalesperson ? selectedSalesperson.cCode : '' },
    skip: !selectedSalesperson, // Skip if no salesperson selected
  });

  const [loadProductionData, { data: productionData, loading, error }] = useLazyQuery(
    GET_DAILY_SALESPERSON_SALES_PRODUCTION,
    {
      variables: { ccode: selectedSalesperson ? selectedSalesperson.cCode : '' },
    }
  );

  const [loadPricingData, { data: productionPricingData, loading: loadingPricing, error: errorPricing }] = useLazyQuery(
    GET_DAILY_SALESPERSON_PRICING_PRODUCTION,
    {
      variables: { ccode: selectedSalesperson ? selectedSalesperson.cCode : '' },
    }
  );

  // Set the initial active tab based on the 'tab' parameter in the URL
  useEffect(() => {
    if (defaultTab) {
      const tabIndex = parseInt(defaultTab, 10); // Convert the 'tab' parameter to an integer
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) { // Updated to accommodate new tab
        setActiveTab(tabIndex.toString());
      }
    }
  }, [defaultTab]);

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);

    // Trigger loading of production data when tab 2 is selected
    if (newValue === '2' && selectedSalesperson) {
      loadProductionData();
    }

    // Trigger loading of pricing data when tab 3 is selected
    if (newValue === '3' && selectedSalesperson) {
      loadPricingData();
    }
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomer(value);
  };

  const handleCalendarTabChange = (newValue) => {
    setActiveCalendarTab(newValue);
  };

  const handleSalespersonSelect = (salesperson) => {
    console.log('Salesperson selected in TabSet:', salesperson); // Debugging
    setSelectedSalesperson(salesperson);
  };

  // Define activities configuration for Production
  const activitiesConfig = useMemo(() => [
    {
      type: 'QUOTES',
      color: '#1976d2',
      dataFields: {
        docCount: 'quoteDocCount',
        lineCount: 'quoteLineCount',
        total: 'quoteTotal',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: SalespersonQuoteDetail,
    },
    {
      type: 'SALES',
      color: '#2D875D',
      dataFields: {
        docCount: 'salesOrderDocCount',
        lineCount: 'salesOrderLineCount',
        total: 'salesOrderTotal',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: SalespersonSalesDetail,
    },
    {
      type: 'CALLS',
      color: '#9C4EE5',
      dataFields: {
        docCount: 'callDocCount',
        lineCount: 'callCount',
        total: 'callTotal',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: SalespersonCallDetail,
    },
  ], []);

  // Define activities configuration for Pricing
  const pricingActivitiesConfig = useMemo(() => [
    {
      type: 'RFQ-PURCHASES',
      color: '#3B5998',
      fontColor: 'black',
      dataFields: {
        docCount: 'purchaseRFQDocCount',
        lineCount: 'purchaseRFQLineCount',
        total: '',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: SalespersonPurchaseRFQsDetail,
    },
    {
      type: 'RFQ-REPAIRS',
      color: '#B87333',
      fontColor: 'black',
      dataFields: {
        docCount: 'repairRFQDocCount',
        lineCount: 'repairRFQLineCount',
        total: '',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: SalespersonRepairsRFQsDetail,
    },
    {
      type: 'PRICING',
      color: '#A23E48',
      fontColor: 'black',
      dataFields: {
        docCount: '',
        lineCount: 'pricingEvents',
        total: '',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: SalespersonPEDetail,
    },
  ], []);

  const assignedAccounts = useMemo(() => {
    if (assignedAccountsData?.assignedAccounts) {
      return assignedAccountsData.assignedAccounts.map(item => item.name);
    }
    return [];
  }, [assignedAccountsData]);

  // Format the production data as needed for CalendarMasterDetail
  const formattedProductionData = useMemo(() => {
    if (!productionData) return [];
    return productionData.dailySalespersonSalesProduction.map((item) => ({
      date: item.date,
      ...item, // Spread all fields dynamically
    }));
  }, [productionData]);

  // Format the production data as needed for CalendarMasterDetail
  const formattedProductionPricingData = useMemo(() => {
    if (!productionPricingData) return [];
    console.log('pricing data: ', productionPricingData);
    return productionPricingData.dailySalespersonPricingProduction.map((item) => ({
      date: item.date,
      ...item, // Spread all fields dynamically
    }));
  }, [productionPricingData]);

  return (
    <Stack gap={0} style={{  width: '100%', height: '100%' }}>
      {/* Salesperson Title and Select - very tight spacing */}
      <Group
        justify="space-between"
        align="center"
       
        style={{
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Text
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
          }}
        >
          {selectedSalesperson
            ? `${selectedSalesperson.fullName}`
            : 'No Salesperson Selected'}
        </Text>

        <SalespersonSelect
          onSalespersonSelect={handleSalespersonSelect}
          defaultSalespersonCode={defaultSalespersonCode}
        />
      </Group>

      {/* Tabs section - minimal spacing */}
      <Group
        justify="space-between"
        align="center"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          padding: '0',
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          styles={{
            root: { flex: 1 },
            list: { borderBottom: 'none' },
            tab: {
              color: '#b3b3b3',
              '&[data-active]': {
                color: '#ffffff',
                fontWeight: 'bold',
              },
              padding: '8px 12px', // Reduced padding
            },
          }}
        >
          <Tabs.List>
            <Tabs.Tab value="0">Profile</Tabs.Tab>
        {/*     <Tabs.Tab value="1">Sales History</Tabs.Tab> */}
            <Tabs.Tab value="2">Quotes, Sales, & Calls</Tabs.Tab>
            <Tabs.Tab value="3">RFQs & Pricing</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Tab indicators - minimal spacing */}
        <Group gap="xs" p="xs">
          {activeTab === '0' && (
            <Box style={{ color: '#ceaf07' }}>
            </Box>
          )}
          {activeTab === '1' && (
            <Box style={{ color: '#ceaf07' }}>
            </Box>
          )}
          {activeTab === '2' && (
            <Group gap="xs">
              <Box style={{ color: '#ceaf07' }}>
              </Box>
              <Group gap={2}>
                <IconCircleFilled size={12} color="white" />
                <IconCircle size={12} color="white" />
              </Group>
            </Group>
          )}
          {activeTab === '3' && (
            <Group gap="xs">
              <Box style={{ color: '#ceaf07' }}>
              </Box>
              <Group gap={2}>
                <IconCircle size={12} color="white" />
                <IconCircleFilled size={12} color="white" />
              </Group>
            </Group>
          )}
        </Group>
      </Group>

      {/* Tab content - no padding, full height */}
      <Box
        style={{
          flexGrow: 1,
          overflow: 'auto',
          padding: 0,
          height: '100%'
        }}
      >
        {activeTab === '0' && (
          <SalespersonProfile selectedSalesperson={selectedSalesperson} />
        )}
        
        {activeTab === '1' && (
          selectedSalesperson ? (
            <SalesHistoryChart ccode={selectedSalesperson.cCode} />
          ) : (
            <Text p="sm">
              Please select a salesperson to view sales history.
            </Text>
          )
        )}
        
        {activeTab === '2' && (
          selectedSalesperson ? (
            loading ? (
              <Text p="sm">
                Loading Production Data...
              </Text>
            ) : error ? (
              <Text p="sm" c="red">
                Error loading production data.
              </Text>
            ) : (
              <div>
                <CalendarMasterDetail
                  businessKey={selectedSalesperson.cCode}
                  data={formattedProductionData}
                  currentYear={new Date().getFullYear()}
                  currentMonth={new Date().getMonth()}
                  onMonthChange={(year, month) => {
                    console.log(`Month changed to: ${month + 1}/${year}`);
                  }}
                  activities={activitiesConfig}
                />
              </div>
            )
          ) : (
            <Text p="sm">
              Please select a salesperson to view production data.
            </Text>
          )
        )}
        
        {activeTab === '3' && (
          selectedSalesperson ? (
            loadingPricing ? (
              <Text p="sm">
                Loading Production Data...
              </Text>
            ) : errorPricing ? (
              <Text p="sm" c="red">
                Error loading production data.
              </Text>
            ) : (
              <div>
                <CalendarMasterDetail
                  businessKey={selectedSalesperson.cCode}
                  data={formattedProductionPricingData}
                  currentYear={new Date().getFullYear()}
                  currentMonth={new Date().getMonth()}
                  onMonthChange={(year, month) => {
                    console.log(`Month changed to: ${month + 1}/${year}`);
                  }}
                  activities={pricingActivitiesConfig}
                />
              </div>
            )
          ) : (
            <Text p="sm">
              Please select a salesperson to view production data.
            </Text>
          )
        )}
      </Box>
    </Stack>
  );
};

export default SalespersonTabSet;